import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Dealer, DealerWithGroup} from '../../../../domain/data/customer-client-response.data';
import {CustomerClientRequest} from '../../../../domain/data/customer-client-request.data';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, distinctUntilChanged, Observable, of, switchMap} from 'rxjs';

@Component({
    selector: 'app-preferred-dealer-customer-update',
    imports: [FormsModule, ReactiveFormsModule],
    templateUrl: './preferred-dealer-customer-update.component.html'
})
export class PreferredDealerCustomerUpdateComponent implements OnInit, OnChanges {
  @Input() vehicleBrand: 'toyota' | 'lexus' = 'toyota';
  @Input() customerConsentRequest: CustomerClientRequest;
  @Input() groupDealerList: Dealer[];
  @Input() allDealerList: DealerWithGroup[];
  @Input() allDealerListLexus: DealerWithGroup[];
  @Output() didChange = new EventEmitter<void>();
  @Output() dealerAdded = new EventEmitter<Dealer>();

  dealerControl = new FormControl('');
  filteredDealers: Dealer[] = [];
  dropdownVisible = false;
  currentSelectedDealer?: Dealer;
  selectedDealer?: Dealer;

  @ViewChild('inputElement') inputElement?: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    if (this.vehicleBrand === 'toyota') {
      this.selectedDealer = this.customerConsentRequest.preferredDealer ?? null;

      this.dealerControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => this.filterDealers(value))
      ).subscribe(filtered => {
        this.filteredDealers = filtered;
        const inputLength = this.dealerControl.value ? this.dealerControl.value.length : 0;
        this.dropdownVisible = filtered.length > 0 || inputLength > 2;
      });
    } else if (this.vehicleBrand === 'lexus') {
      this.selectedDealer = this.customerConsentRequest.preferredDealerLexus ?? null;

      this.dealerControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => this.filterDealers(value))
      ).subscribe(filtered => {
        this.filteredDealers = filtered;
        const inputLength = this.dealerControl.value ? this.dealerControl.value.length : 0;
        this.dropdownVisible = filtered.length > 0 || inputLength > 2;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDealer']) {
      if (this.selectedDealer) {
        this.currentSelectedDealer = this.selectedDealer;
        this.dealerControl.setValue(this.selectedDealer.name, { emitEvent: false });
      } else {
        this.currentSelectedDealer = undefined;
        this.dealerControl.setValue('', { emitEvent: false });
      }
    }
  }

  filterDealers(query: string): Observable<Dealer[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    const allDealers: DealerWithGroup[] = [];
    if (this.vehicleBrand === 'toyota') {
      allDealers.push(...this.allDealerList || []);
    } else if (this.vehicleBrand === 'lexus') {
      allDealers.push(...this.allDealerListLexus || []);
    }
    const normalizedQuery = query.toLowerCase();

    const filtered = allDealers.filter(dealer =>
      dealer.name.toLowerCase().includes(normalizedQuery) ||
      dealer.street.toLowerCase().includes(normalizedQuery) ||
      dealer.postcode.toLowerCase().includes(normalizedQuery) ||
      dealer.city.toLowerCase().includes(normalizedQuery)
    );

    return of(filtered);
  }

  get noResults(): boolean {
    return this.filteredDealers.length === 0 && this.dealerControl.value?.length > 2;
  }

  onFocus() {
    const inputLength = this.dealerControl.value ? this.dealerControl.value.length : 0;
    if (this.filteredDealers.length > 0 || inputLength > 2) {
      this.dropdownVisible = true;
    }
  }

  onBlur() {
    setTimeout(() => {
      this.dropdownVisible = false;

      const currentValue = this.dealerControl.value?.trim() ?? '';
      if (this.currentSelectedDealer && currentValue !== this.currentSelectedDealer.name) {
        this.dealerControl.setValue(this.currentSelectedDealer.name, { emitEvent: false });
      }

      if (!this.currentSelectedDealer && currentValue !== '') {
        this.dealerControl.setValue('', { emitEvent: false });
      }
    }, 200);
  }

  selectDealer(dealer: Dealer) {
    if (this.vehicleBrand === 'toyota') {
      this.customerConsentRequest.preferredDealer = dealer;
    } else if (this.vehicleBrand === 'lexus') {
      this.customerConsentRequest.preferredDealerLexus = dealer;
    }
    const combinedDealerList: DealerWithGroup[] = [
      ...this.allDealerList ?? [],
      ...this.allDealerListLexus ?? []
    ];

    if (dealer.dealerGroup) {
      const incl = this.groupDealerList && this.groupDealerList.find((d) => {
        return d.dealerGroup === dealer.dealerGroup;
      });

      if (!incl) {
        // find all dealers with the same dealerGroup and add them to the connected dealer list
        const dealersInSameGroup = combinedDealerList.filter(d =>
          d.dealerGroup === dealer.dealerGroup
        );
        dealersInSameGroup.forEach(d => {
          const isDealerFromLexusList = this.allDealerListLexus?.some(lexusDealer =>
            lexusDealer.dealerNumber === d.dealerNumber
          );
          if (isDealerFromLexusList) {
            d.lexus = true;
          } else {
            d.toyota = true;
          }
        })
        this.customerConsentRequest.connectedDealerList.push(...dealersInSameGroup);

        // add the main dealer to group dealers
        const mainDealerInGroup = dealersInSameGroup.find(d =>
          d.dealerNumber === d.dealerGroup
        );
        if (!mainDealerInGroup.fullCompanyNameAsList) {
          mainDealerInGroup.fullCompanyNameAsList = [];
        }
        mainDealerInGroup.fullCompanyNameAsList.push(mainDealerInGroup.groupName);
        this.customerConsentRequest.groupDealerList.push(mainDealerInGroup);
      }
    }

    this.didChange.emit();
    this.currentSelectedDealer = dealer;
    this.dealerControl.setValue(undefined, { emitEvent: false });
    this.dropdownVisible = false;
  }

  onInputChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;

    if (!value) {
      this.currentSelectedDealer = undefined;
    }
  }
}
